import { orgQueryKeys } from '@/config/queryKeys';
import { updateOrganization } from '@/services/organizations';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useStore } from '@tanstack/react-store';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { OrgFormApi } from './formTypes';
import type { OrgFormValues } from './schema';
import { createOrgSchema } from './schema';

/** Duration (ms) for success/error toasts — long enough to comfortably read. */
const TOAST_DURATION = 8000;

export function useEditOrganization({
  orgId,
  initialValues,
  onSave,
}: {
  orgId: string;
  initialValues: OrgFormValues;
  onSave?: (values: OrgFormValues) => void;
}): { form: OrgFormApi; hasAttemptedSubmit: boolean } {
  const { t } = useTranslation('orgs');
  const queryClient = useQueryClient();
  const schema = useMemo(
    // Alias is locked after creation, so the edit form has no duplicate-alias set to check against.
    () => createOrgSchema(t, undefined, { originalExpiration: initialValues.subscription.expiration }),
    [t, initialValues.subscription.expiration],
  );

  const form = useForm({
    formId: 'organizationDetailsEdit',
    defaultValues: initialValues,
    validators: {
      onMount: schema,
      onChange: schema,
    },
    onSubmit: async ({ value }) => {
      const payload = {
        name: value.name,
        email_domain: value.domain,
        alias: value.alias,
        address: value.address,
        primary_contact: {
          first_name: value.primaryContact.firstName,
          last_name: value.primaryContact.lastName,
          phone: value.primaryContact.phone,
          email: value.primaryContact.email,
        },
        it_contact: {
          first_name: value.itContact.firstName,
          last_name: value.itContact.lastName,
          phone: value.itContact.phone,
          email: value.itContact.email,
        },
        subscription: {
          is_active: value.subscription.active,
          expires_at: value.subscription.expiration,
        },
      };

      const apiPromise = updateOrganization(orgId, payload);

      // toast.promise shows loading → success/error with a long auto-dismiss.
      // Only the API call is wrapped so that post-success side-effects
      // (cache invalidation, form reset) don't affect the toast state.
      toast.promise(apiPromise, {
        loading: t('edit_org_saving'),
        success: t('edit_org_success'),
        error: (err: unknown) =>
          err instanceof Error && err.message
            ? t('edit_org_error_detail', { message: err.message })
            : t('edit_org_error'),
        duration: TOAST_DURATION,
      });

      try {
        await apiPromise;
        // Invalidate both the org list and this specific org's detail query.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: orgQueryKeys.list() }),
          queryClient.invalidateQueries({ queryKey: orgQueryKeys.detail(orgId) }),
        ]);
        // Reset to saved values so isDirty → false, disabling the Save button.
        form.reset(value);
        onSave?.(value);
      } catch {
        // Error already handled and displayed by toast.promise above.
      }
    },
    onSubmitInvalid: () => {
      requestAnimationFrame(() => {
        const firstInvalid = document.querySelector('[aria-invalid="true"]') as HTMLElement | null;
        firstInvalid?.focus();
      });
    },
  });

  // `form` is a stable reference from useForm — adding it satisfies exhaustive-deps
  // without causing extra re-runs. Reset whenever the parent pushes new initialValues,
  // but only when the user hasn't made any edits yet. Without this guard, a TanStack
  // Query background refetch that returns a new `org` object reference (even with
  // identical field values) produces a new `initialValues` reference, which triggers
  // this effect and calls form.reset() — wiping isDirty back to false and disabling
  // the save button while the user is actively editing.
  useEffect(() => {
    if (!form.state.isDirty) {
      form.reset(initialValues);
    }
  }, [form, initialValues]);

  const hasAttemptedSubmit = useStore(form.baseStore, s => s.submissionAttempts > 0);

  return { form: form as unknown as OrgFormApi, hasAttemptedSubmit };
}
